import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from './user.service';
import { CacheService } from './cache.service';
import { NotificationMessage } from '../services/model/notification-message.model';
import { DialogType } from '../services/model/dialog-type.enum';
import { ExecutingTaskMonitorService } from './executing-task-monitor.service';

@Injectable({
  providedIn: 'root',
})
export class GlobalErrorHandlerService implements ErrorHandler {
  private readonly _userService: UserService;

  constructor(
    private readonly _injector: Injector,
    private readonly _cache: CacheService,
    private readonly _executingTaskSvc: ExecutingTaskMonitorService
  ) {
    this._userService = this._injector.get(UserService);
  }

  public handleError(error: Error | HttpErrorResponse): void {
    console.error('handleError', error);

    const errorMessage: string = 'An error has occurred; Please try again.';
    let errorDetail: string = '';

    if (error instanceof HttpErrorResponse) {
      // server side error
      let eventId = (error as any)?.error?.cdiEventId;
      if (error.status != null && error.status != undefined) {
        if (error.status === 0) {
          errorDetail = `System Not Available`;
        } else {
          if (error.error && error.status === 401) {
            errorDetail = '401. Authentication Failed. Please login.';
          } else if (error.error && error.status === 403) {
            errorDetail =
              '403. Authorization Failed. You do not have permissions to access resource.';
          } else if (error.error && error.status === 400) {
            eventId = this._cache.generateGuid();
            errorDetail = `${this.parseBadRequest(error)}`;
          } else if (error.error && error.status === 404) {
            errorDetail = `Status Code: 404; Not Found`;
          } else if (error.error && error.status === 405) {
            errorDetail = `Status Code: 405; Method Not Allowed`;
          } else if (error.error && error.status === 429) {
            console.error('429', error);
          } else if (error.error && error.status === 500) {
            errorDetail = `${this.parseErrorMessage(error)}`;
          } else if (error.error && error.status === 512) {
            errorDetail = this.parse512Error(error.error, this._userService);
          } else if (error.error && error.status === 513) {
            errorDetail = this.custom500Error(error.error, 513);
          } else if (error.error && error.status === 514) {
            errorDetail = this.custom500Error(error.error, 514);
          } else if (error.error && error.status === 515) {
            errorDetail = this.custom500Error(error.error, 515);
          } else if (error.error && error.status === 516) {
            errorDetail = this.custom500Error(error.error, 516);
          } else if (error.error && error.status === 517) {
            errorDetail = this.custom500Error(error.error, 517);
          } else if (error.error && error.status === 518) {
            errorDetail = this.custom500Error(error.error, 518);
          } else if (error.error && error.status === 519) {
            errorDetail =
              'Error occured while generating Order Form; please try again later.';
          } else if (error.error && error.status === 531) {
            errorDetail =
              'Unable to set administrative hold. Please review logs further details.';
          } else if (error.error && error.status === 532) {
            errorDetail =
              'Unable to release administrative hold. Please review logs further details.';
          }
        }
        this._executingTaskSvc.showFullPageSpinner.next(false);
        this.showErrorMessage([errorDetail], eventId);
      }
    } else {
      // client side error
      this.showErrorMessage([error.message], 'Cli');
    }
  }

  private showErrorMessage(message: string[], cdiEventId: string) {
    this._cache.showDialog.next(
      new NotificationMessage(DialogType.error, 'Error', message, cdiEventId)
    );
  }

  private parse512Error(text: any, user: UserService): string {
    let message =
      'Unable to place order with BSSV. Please refresh your browser and try again later.';
    try {
      let custmomMessage: string = '';
      if (text.isTransactionLocked === true) {
        if (user.userProfile.companyCode == '00015') {
          custmomMessage =
            'Please contact Dealer Services for further assistance.';
          message = `${text.message} ${custmomMessage}`;
        } else {
          custmomMessage =
            'Please contact European Sales Support for further assistance.';
          message = `${text.message} ${custmomMessage}`;
        }
      } else {
        message = `${message}`;
      }
    } catch (error) {
      console.error('custom500Error', error);
    }
    return message;
  }

  private custom500Error(text: any, errorNumber: number): string {
    let message: string = '';
    try {
      message = `${text.message}`;
    } catch (error) {
      console.error('custom500Error', error);
    }
    return message;
  }

  private parseErrorMessage(err: HttpErrorResponse): string {
    let message: string = '';
    try {
      if (err.error != null && err.error != undefined) {
        if (
          (err.error.cdiEventId === null ||
            err.error.cdiEventId === undefined) &&
          (err.error.message === null || err.error.message === undefined)
        ) {
          message = `${err.error}`;
        } else {
          message = `${err.error.message}`;
        }
      }
    } catch (error) {
      console.error('parseErrorMessage', error);
      return 'unavailable.';
    }
    return message;
  }

  private parseBadRequest(err: any): string {
    let errorMessage: string = 'Validation Error';
    try {
      if (err.error != null && err.error != undefined) {
        if (
          err?.error?.title?.includes('One or more validation errors occurred')
        ) {
          errorMessage = `One or more validation errors occurred.\n ${this.parseValidationError(
            err.error.errors
          )}`;
        }
      }
    } catch (error) {
      console.error('parseBadRequest', error);
      return 'unavailable.';
    }
    return errorMessage;
  }

  private parseValidationError(data: any): string {
    let validationErrors: string = '';
    try {
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          validationErrors = validationErrors + `${data[key]}\n`;
        }
      }
    } catch (error) {
      console.error('parseValidationError', error);
      return 'Unable to Parse Validation Error';
    }
    return validationErrors;
  }
}
